/* A notice or a question in ONAM's own dialog (FEATURES.curationSkin). InfoPopup hands its plain-text uses here:
   warnings that ask whether to go on, errors, validation messages and short information. Its richer uses (tiles,
   tables, tabs, refresh, or content of their own) stay as they are.

   The kind sits in the small label above, in its colour (Warning in caution, Error in error, the rest in notice);
   the first paragraph of the message becomes the heading, and the rest reads beneath it, beside the buttons that
   answer it. Escape, the close button or a click outside does what Cancel does (or closes, when there is no Cancel). */

import React, { ReactNode, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { OI } from "../mark";
import { useDialogFocus } from "../focus";

export type NoticeKind = "warning" | "error" | "info";

const GLYPH: Record<NoticeKind, string> = { warning: "warn", error: "error", info: "info" };

/** Splits a plain message into a heading and what follows. The heading is the first paragraph when it is short, or
    its first line when that line introduces details beneath it ("Duplicate condition detected:" followed by
    "Attribute: …" lines). JSON-looking text keeps the kind as its heading and is shown whole beneath. */
export function splitMessage(text: string | undefined, fallbackTitle: string): { title: string; rest: string[] } {
  const t = (text || "").trim();
  if (!t || /[{[]/.test(t)) return { title: fallbackTitle, rest: t ? [t] : [] };
  const paras = t.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  const lines = paras[0].split("\n").map((l) => l.trim()).filter(Boolean);
  if (lines.length > 1 && lines[0].length <= 140) return { title: lines[0].replace(/:$/, ""), rest: [lines.slice(1).join("\n"), ...paras.slice(1)] };
  if (paras[0].length > 140) return { title: fallbackTitle, rest: paras };
  return { title: paras[0], rest: paras.slice(1) };
}

/** One paragraph of a notice: "Name: value" lines become a small list of facts; anything else reads as text. */
export function NoticePara({ text, json }: { text: string; json?: (t: string) => ReactNode }) {
  if (json && /[{[]/.test(text)) return <>{json(text)}</>;
  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
  const facts = lines.map((l) => l.match(/^([^:]{1,40}):\s+(.+)$/));
  if (lines.length > 1 && facts.every(Boolean)) {
    return <dl className="nt-dl">{facts.map((m, i) => <React.Fragment key={i}><dt>{m![1]}</dt><dd>{m![2]}</dd></React.Fragment>)}</dl>;
  }
  return <p>{text}</p>;
}

export default function OnamNotice({
  kind, label, title, children, primary, onPrimary, cancel, onCancel, danger,
}: {
  kind: NoticeKind; label: string; title: string; children?: ReactNode;
  primary: string; onPrimary: () => void; cancel?: string; onCancel?: () => void; danger?: boolean;
}) {
  const box = useRef<HTMLDivElement>(null);
  const go = useRef<HTMLButtonElement>(null);
  const dismiss = onCancel || onPrimary;
  useDialogFocus(box, true);
  useEffect(() => { go.current?.focus(); }, []);
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); dismiss(); } };
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }, [dismiss]);
  const id = useRef(`nt-${Math.random().toString(36).slice(2, 8)}`).current;
  // when the message has no heading of its own, the kind is the heading (no "Information" twice)
  const same = title.trim().toLowerCase() === label.trim().toLowerCase();
  return createPortal(
    <div className="onam-app onam-notice">
      <div className="mwrap in" onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}>
        <div className={`modal nt-box nt-${kind}`} ref={box} role={cancel ? "alertdialog" : "dialog"} aria-modal="true" aria-labelledby={same ? `${id}-k` : `${id}-h`} aria-describedby={children ? `${id}-b` : undefined}>
          <div className="m-h">
            <div className="stack">
              <span className="label nt-kind" id={`${id}-k`}><OI n={GLYPH[kind]} size={14} />{label}</span>
              {same ? null : <h2 id={`${id}-h`}>{title}</h2>}
            </div>
            <button type="button" className="x" aria-label="Close" title="Close" onClick={dismiss}><OI n="close" /></button>
          </div>
          {children ? <div className="m-b nt-b" id={`${id}-b`}>{children}</div> : null}
          <div className="m-f">
            {cancel ? <button type="button" className="btn ghost" onClick={onCancel}>{cancel}</button> : null}
            <button type="button" ref={go} className={`btn${danger ? " danger" : ""}`} onClick={onPrimary}>{primary}</button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
